/**
 * The canonical semantic color roles shared by Neucleos surfaces.
 *
 * This list intentionally mirrors Cleo Code's existing theme-file contract.
 * Adding or removing a role is a file-format change, not an app-local styling
 * choice.
 */
export const THEME_COLOR_ROLES = [
  "canvas",
  "chrome",
  "toolbar",
  "toolbarForeground",
  "toolbarBorder",
  "toolbarControl",
  "toolbarControlForeground",
  "toolbarControlHover",
  "surface",
  "surfaceRaised",
  "surfaceOverlay",
  "text",
  "textMuted",
  "border",
  "input",
  "focus",
  "accent",
  "accentForeground",
  "secondary",
  "secondaryForeground",
  "muted",
  "mutedForeground",
  "placeholder",
  "secondaryLabel",
  "iconMuted",
  "error",
  "errorForeground",
  "errorSurface",
  "warning",
  "warningForeground",
  "warningSurface",
  "update",
  "updateForeground",
  "updateSurface",
  "accentSurface",
  "accentSurfaceForeground",
  "messageSurface",
  "messageForeground",
  "messageAction",
  "messageActionForeground",
  "messageActionHover",
  "codeBackground",
  "codeForeground",
  "sidebar",
  "sidebarForeground",
  "sidebarMutedForeground",
  "sidebarControlSurface",
  "sidebarRowHover",
  "sidebarRowActive",
  "sidebarRowSelected",
  "sidebarBorder",
  "terminalBackground",
  "terminalForeground",
  "terminalCursor",
  "terminalSelection",
  "terminalScrollbar",
  "terminalScrollbarHover",
] as const;

export type ThemeColorRole = (typeof THEME_COLOR_ROLES)[number];
export type ThemeCssVariable = `--ncl-${string}`;
export type ThemeColorValues = Readonly<Record<ThemeColorRole, string>>;

function camelToKebab(value: string): string {
  return value.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

/** The stable CSS custom property for each theme-file role. */
export const THEME_CSS_VARIABLES = Object.freeze(
  Object.fromEntries(
    THEME_COLOR_ROLES.map((role) => [role, `--ncl-${camelToKebab(role)}`]),
  ),
) as Readonly<Record<ThemeColorRole, ThemeCssVariable>>;

export function themeCssVariable(role: ThemeColorRole): ThemeCssVariable {
  return THEME_CSS_VARIABLES[role];
}

/** A semantic color reference suitable for component props and inline styles. */
export function themeColor(role: ThemeColorRole): string {
  return `var(${themeCssVariable(role)})`;
}

/** Project a complete app palette into the canonical custom-property names. */
export function themeColorsToCssVariables(
  colors: ThemeColorValues,
): Readonly<Record<ThemeCssVariable, string>> {
  return Object.freeze(
    Object.fromEntries(
      THEME_COLOR_ROLES.map((role) => [THEME_CSS_VARIABLES[role], colors[role]]),
    ),
  ) as Readonly<Record<ThemeCssVariable, string>>;
}

/*
 * Non-color token families — added 2026-08-29 (Arc 6 / PS5). Additive
 * beside the 57 color roles: public-site sections and Canvas faces read
 * type, radius, density, and motion tokens; values come from themes and
 * doors, never from components.
 */

export const THEME_TYPE_SCALE_ROLES = [
  "display",
  "headline",
  "title",
  "body",
  "label",
  "caption",
  "code",
] as const;

export const THEME_RADIUS_ROLES = ["control", "field", "card", "surface", "pill"] as const;

export const THEME_DENSITY_ROLES = ["row", "gap", "inset", "stack"] as const;

export const THEME_MOTION_ROLES = ["instant", "quick", "calm", "slow"] as const;

export const THEME_FAMILY_PREFIXES = Object.freeze({
  typeScale: "type",
  radius: "radius",
  density: "density",
  motion: "motion",
} as const);

export type ThemeFamilyName = keyof typeof THEME_FAMILY_PREFIXES;

export const THEME_FAMILY_ROLES = Object.freeze({
  typeScale: THEME_TYPE_SCALE_ROLES,
  radius: THEME_RADIUS_ROLES,
  density: THEME_DENSITY_ROLES,
  motion: THEME_MOTION_ROLES,
} as const) satisfies Readonly<Record<ThemeFamilyName, readonly string[]>>;

/** The stable CSS custom property for a non-color family role. */
export function themeFamilyCssVariable<F extends ThemeFamilyName>(
  family: F,
  role: (typeof THEME_FAMILY_ROLES)[F][number],
): ThemeCssVariable {
  return `--ncl-${THEME_FAMILY_PREFIXES[family]}-${camelToKebab(role)}`;
}

/*
 * The Cleo slot — the 2026-08-29 ruling revision enacted: the cockpit
 * legend's "FIXED — Cleo's" becomes "reserved — Cleo's". The slot is
 * promoted into the shared contract with today's teal as the default
 * value; free variation of the value waits for a named need.
 *
 * D-CI1 note: the values below encode the RECOMMENDED two-constant shape
 * (cleoIdentity = interface teal family; cleoMark = mark green on its
 * field) — named as the recommendation, per the CB18 precedent. A contrary
 * D-CI1 ruling re-articulates values, never structure.
 */

/**
 * cleoIdentity: Cleo's interface family.
 *
 * DATED RE-ARTICULATION 2026-09-02 (the principal, with the Neucleos brand
 * swatch: "I want to change (for now) Cleo's color to the neucleos orange
 * which is roughly f84e19"): the interface TEAL family (the cockpit donor
 * ramp — light #9ED4D0, mid #4E9A94, deep #2E4744) is superseded by the
 * Neucleos ORANGE family. VALUES change; the D-CI1 two-constant STRUCTURE
 * (cleoIdentity family + cleoMark on its field) is untouched, exactly as
 * that ruling allows. "For now" is the principal's own word — this seat is
 * where a return, or another hue, is re-articulated.
 *
 * `light` is the principal's hex byte-exact: it is the value the interface
 * actually reads (`--ncl-cleo-identity` → the app's `--cleo-identity`: the
 * Cleo card's ring, the rail tab, the folded pill), so Cleo now READS as the
 * named orange rather than a tint of it. `mid` and `deep` are its dim and
 * edge steps at the same hue.
 */
/*
 * DATED RE-ARTICULATION 2026-09-14 (the principal, in the Apple design review:
 * "I do agree that Cleo's orange looks red, so one idea was to adopt the most
 * yellow hue that's in the mark"; "use your design expertise and make the
 * decisions"): the family moves to the hue of the mark sweep's LIGHT stop
 * (#FFB380, oklch hue 54.4) — the yellow end of her own mark — at the
 * interface's lightness and chroma, so she is unmistakable beside error red
 * and never mistaken for a warning. Register: GOALS/GOALS-DESIGN.html#hig5.
 * VALUES change; the two-constant STRUCTURE is untouched.
 */
export const CLEO_IDENTITY_SOURCE_HEX = Object.freeze({
  light: "#EF8530",
  mid: "#B66018",
  deep: "#663306",
} as const);

/**
 * cleoMark: the pinned mark on its field (cleo-mark-one-instrument).
 *
 * DATED 2026-09-02 with the family above: the mark GREEN #5BAD97 is
 * superseded by the family's own deeper orange, so the seal cannot read as a
 * different identity from the interface around it. Its field is unchanged —
 * the field was always a near-black ground, never a teal. The mark keeps its
 * own constant (the two-constant shape) and sits a step deeper than the
 * identity light, the way the green sat a step from the teal.
 */
export const CLEO_MARK_SOURCE_HEX = Object.freeze({
  // DATED 2026-09-14: one step deeper than the identity light at the same hue
  // (oklch 0.66 0.16 54.5); the field is unchanged.
  mark: "#DA7210",
  field: "#0B0E0D",
} as const);

/**
 * cleoMarkSweep: the live annunciator's ring blend (FD-CAN-32, dated
 * 2026-09-02 — the principal, of the flat orange ring against the design of
 * record: "I see this is actually a bit of a blend. Can we replicate
 * something like we had in the design?").
 *
 * The design's own three stops on its mark arc are #FFB380 → #FF5A1F →
 * #FF3D8A (peach → orange → pink, drawn along the diagonal). Re-anchored
 * here: the CORE stop is Cleo's own identity — the hex the principal chose
 * ("I like whatever color we chose here") — between the design's peach and
 * its pink, so the blend is the design's while the color is hers. These are
 * the mark's own constants beside the flat `mark` above: the flat value
 * still answers every reader that wants ONE Cleo color (favicons, the seal),
 * and only the live annunciator paints the sweep.
 */
export const CLEO_MARK_SWEEP_SOURCE_HEX = Object.freeze({
  light: "#FFB380",
  core: "#EF8530", // DATED 2026-09-14: the core is her identity light, re-anchored with it
  far: "#FF3D8A",
} as const);

/**
 * Cleo's channels, pinned from the named source and never recomputed at read
 * time — display values are never identity; the durable guarantee is that
 * Cleo is unmistakable at every size. Lightness is pinned the way terra's is.
 *
 * DATED 2026-09-02: measured from the identity family's LIGHT (#F84E19, the
 * hex the principal named as Cleo's color), where the teal slot measured its
 * MID — because the named hex IS the interface value now, so `--ncl-cleo`
 * and `--ncl-cleo-identity` are one color rather than two steps of a ramp.
 * A derivation note re-articulated by ruling, never a structural change.
 */
// DATED 2026-09-14: measured from the identity light #EF8530 (oklch 0.720
// 0.160 54.5); the band below re-centers with the hue by construction.
export const CLEO_ACCENT_LIGHTNESS = 0.72;
export const CLEO_ACCENT_CHROMA = 0.16;
export const CLEO_ACCENT_HUE = 54.5;

/**
 * The distinctness constraint (dated 2026-08-29): a reserved band around
 * Cleo's hue that World and user accents may not enter, so terra can never
 * camouflage her mark. ±24° covers the whole identity family — light 35.9,
 * deep 35.5, mark 38.0 (dated 2026-09-02, the orange re-articulation; the
 * teal family's 190.4 / 186.5 / 174.1 before it, when the band reserved the
 * teals instead) — with perceptual margin; revise by dated
 * re-articulation, never silently. ONE definition, estate-wide: TH6's user
 * agentAccents band and the World-accent write law both consume this
 * constant.
 */
export const CLEO_RESERVED_HUE_BAND = Object.freeze({
  centerHue: CLEO_ACCENT_HUE,
  deltaDegrees: 24,
} as const);

/** Shortest wheel distance between two hues, any finite input. */
export function hueDistanceDegrees(a: number, b: number): number {
  const raw = Math.abs((((a - b) % 360) + 360) % 360);
  return Math.min(raw, 360 - raw);
}

export function isHueInsideCleoBand(hue: number): boolean {
  return (
    hueDistanceDegrees(hue, CLEO_RESERVED_HUE_BAND.centerHue) <=
    CLEO_RESERVED_HUE_BAND.deltaDegrees
  );
}

/*
 * The yield law — dated re-articulation 2026-08-31 at the principal's word
 * ("We decided we were going to let the users change the accent"; "a
 * control must never offer a value the door refuses"): the reserved band
 * NO LONGER refuses World-accent writes. The World always wins; CLEO
 * YIELDS — when a World parks inside her band, her accent shifts to the
 * nearest hue at exactly the band's distance from the World's, on the side
 * closest to her home teal. Distinctness is guaranteed by computation,
 * never by refusing the person. The band constant survives as the
 * separation distance; TH6's user agentAccents band keeps its own law
 * until its own ruling.
 */
export interface CleoAccentForWorld {
  readonly hue: number;
  readonly yielded: boolean;
}

export function resolveCleoAccentForWorld(worldAccentHue: number): CleoAccentForWorld {
  const world = (((worldAccentHue % 360) + 360) % 360) || 0;
  if (!isHueInsideCleoBand(world)) {
    return { hue: CLEO_ACCENT_HUE, yielded: false };
  }
  const separation = CLEO_RESERVED_HUE_BAND.deltaDegrees;
  // Round the candidates BEFORE comparing so the choice is deterministic in
  // exact tenths, never a float-noise coin flip: on the exact tie (a World
  // sitting on her home hue) she steps up.
  const up = Math.round(((world + separation) % 360) * 10) / 10;
  const down = Math.round(((((world - separation) % 360) + 360) % 360) * 10) / 10;
  const hue =
    hueDistanceDegrees(up, CLEO_ACCENT_HUE) <= hueDistanceDegrees(down, CLEO_ACCENT_HUE)
      ? up
      : down;
  return { hue, yielded: true };
}

export const CLEO_CSS_VARIABLES = Object.freeze({
  hue: "--ncl-cleo-hue",
  chroma: "--ncl-cleo-chroma",
  lightness: "--ncl-cleo-lightness",
  accent: "--ncl-cleo",
  accentLight: "--ncl-cleo-light",
  accentDeep: "--ncl-cleo-deep",
  mark: "--ncl-cleo-mark",
  markField: "--ncl-cleo-mark-field",
  markSweepLight: "--ncl-cleo-mark-sweep-light",
  markSweepCore: "--ncl-cleo-mark-sweep-core",
  markSweepFar: "--ncl-cleo-mark-sweep-far",
} as const);

export type CleoCssVariable = (typeof CLEO_CSS_VARIABLES)[keyof typeof CLEO_CSS_VARIABLES];

/** Project the Cleo slot to its custom properties. Deterministic, no inputs. */
export function resolveCleoIdentity(): Readonly<Record<CleoCssVariable, string>> {
  return Object.freeze({
    [CLEO_CSS_VARIABLES.hue]: String(CLEO_ACCENT_HUE),
    [CLEO_CSS_VARIABLES.chroma]: String(CLEO_ACCENT_CHROMA),
    [CLEO_CSS_VARIABLES.lightness]: String(CLEO_ACCENT_LIGHTNESS),
    [CLEO_CSS_VARIABLES.accent]: `oklch(${CLEO_ACCENT_LIGHTNESS} ${CLEO_ACCENT_CHROMA} ${CLEO_ACCENT_HUE})`,
    [CLEO_CSS_VARIABLES.accentLight]: CLEO_IDENTITY_SOURCE_HEX.light,
    [CLEO_CSS_VARIABLES.accentDeep]: CLEO_IDENTITY_SOURCE_HEX.deep,
    [CLEO_CSS_VARIABLES.mark]: CLEO_MARK_SOURCE_HEX.mark,
    [CLEO_CSS_VARIABLES.markField]: CLEO_MARK_SOURCE_HEX.field,
    [CLEO_CSS_VARIABLES.markSweepLight]: CLEO_MARK_SWEEP_SOURCE_HEX.light,
    [CLEO_CSS_VARIABLES.markSweepCore]: CLEO_MARK_SWEEP_SOURCE_HEX.core,
    [CLEO_CSS_VARIABLES.markSweepFar]: CLEO_MARK_SWEEP_SOURCE_HEX.far,
  }) as Readonly<Record<CleoCssVariable, string>>;
}
